import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["editor", "isDisabled", "menuItems"],
  _excluded2 = ["contextualNodeAttributes"];
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import { NoAuthGatesProvider } from 'ai-components-ui-library/hooks/useIsUngated';
import { Text } from 'foundations-components/components/Text';
import { HR } from 'foundations-components/transitional/components/HR';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { gate } from 'hub-http/gates';
import { useCallback, useMemo, useState } from 'react';
import UITextToolbarButton from 'rich-text-lib/components/editor/UITextToolbarButton';
// @ts-expect-error untyped module
import tinymce from 'tinymce';
import { BreezeSingleStarIcon } from 'foundations-assets';
// @ts-expect-error untyped module
import ToolbarMenu from 'rich-text-lib/components/toolbar/ToolbarMenu';
import { CANVAS_COPILOT_COLLABORATION } from 'tinymce-plugins/constants/toolbar';
import BrandVoiceMenuOptionLabel from '../components/BrandVoiceMenuOptionLabel';
import { SUPER_ADMIN_SCOPE } from '../constants/hscontentgeneration';
import { getCopilotSessionIdFromEditor } from '../hscontentgeneration/utils';
import { hasScope, isUngated } from '../utils/settings';
import { trackInteraction } from '../utils/usageTracking';
import { COLLAB_MODE_INTERACTIONS_TRACKING } from './constants';
import { getIsBrandVoiceCommand, getIsHighlightingText } from './utils';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import styled from 'foundations-theming/utils';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { OPEN_AI_DYNAMIC_TOKEN_MODAL_ACTION } from '../hsaidynamictokens/types';
import { AI_DYNAMIC_TOKEN_SETTINGS } from '../constants/hsaidynamictokens';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getSettingsLink = editor => {
  return `${editor.settings.appUrl}/ai-settings/${PortalIdParser.get()}`;
};
const getIsSuperAdmin = editor => {
  const {
    scopes
  } = editor.settings;
  const isSuperAdmin = hasScope(SUPER_ADMIN_SCOPE, scopes);
  return isSuperAdmin;
};
const handleTrackSettingsClick = editor => {
  trackInteraction(editor, COLLAB_MODE_INTERACTIONS_TRACKING,
  // `action` - (will be added to the eventProperties below)
  'click-settings-link', {
    interactionType: 'toolbar-interaction',
    sessionId: getCopilotSessionIdFromEditor(editor)
  });
};
const TooltipText = ({
  editor,
  isDisabled,
  isSuperAdmin
}) => {
  const editorContent = editor.selection.getContent();
  const tooltipI18nKey = useMemo(() => {
    if (editorContent) {
      return 'TinymcePlugins.hscopilotcollaboration.menuItemBreezeWithSelection';
    }
    return 'TinymcePlugins.hscopilotcollaboration.menuItemBreeze';
  }, [editorContent]);
  if (!isDisabled) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: tooltipI18nKey
    });
  }
  // Disabled and a super admin - show the settings link
  if (isSuperAdmin) {
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "TinymcePlugins.hscopilotcollaboration.toolbar.buttonDisabled.superAdmin_jsx",
      elements: {
        UILink: Link
      },
      options: {
        onClick: () => {
          handleTrackSettingsClick(editor);
          window.open(getSettingsLink(editor));
        },
        external: true
      }
    });
  }
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "TinymcePlugins.hscopilotcollaboration.toolbar.buttonDisabled.default"
  });
};
const ToolbarMenuWrapper = styled.div.withConfig({
  displayName: "controls__ToolbarMenuWrapper",
  componentId: "sc-1lnld11-0"
})(({
  theme
}) => {
  return {
    '& button': {
      padding: theme.space['0']
    }
  };
});
export function GenAndSelectToolbarButton(_ref) {
  let {
      editor,
      isDisabled,
      menuItems
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [isOpen, setIsOpen] = useState(false);
  const handleOnOpenChange = useCallback(() => {
    setIsOpen(prevState => !prevState);
  }, []);
  const isSuperAdmin = getIsSuperAdmin(editor);
  const handleTrackMenuClick = useCallback(() => {
    const highlightedText = editor.selection.getContent({
      format: 'text'
    });
    trackInteraction(editor, COLLAB_MODE_INTERACTIONS_TRACKING,
    // `action` - (will be added to the eventProperties below)
    isOpen ? 'menu-closed' : 'menu-opened', {
      interactionType: 'toolbar-interaction',
      featureCategoryId: highlightedText ? 'content_rewrite' : 'generate_text',
      sessionId: getCopilotSessionIdFromEditor(editor)
      // inApp: supplied by tinyMCE magic
    });
  }, [editor, isOpen]);
  return /*#__PURE__*/_jsx(Tooltip, {
    placement: "bottom",
    disabled: isOpen,
    title: /*#__PURE__*/_jsx(TooltipText, {
      editor: editor,
      isDisabled: isDisabled,
      isSuperAdmin: isSuperAdmin
    }),
    children: /*#__PURE__*/_jsx(UITextToolbarButton, {
      inlineSize: "38px",
      selected: isOpen,
      children: /*#__PURE__*/_jsx(ToolbarMenuWrapper, {
        children: /*#__PURE__*/_jsx(ToolbarMenu, Object.assign({
          editor: editor,
          "data-test-id": "insert-gen-select-button",
          icon: () => /*#__PURE__*/_jsx(IconHolder, {
            marginInlineStart: "-100",
            marginBlockStart: "-50",
            color: isDisabled ? undefined : 'fill-accent-neutral-default',
            icon: BreezeSingleStarIcon
          }),
          onOpenChange: handleOnOpenChange
        }, rest, {
          // rest object has `menuItems = undefined` so we need to overwrite that
          menuItems: menuItems,
          disabled: isDisabled,
          onClick: handleTrackMenuClick
        }))
      })
    })
  });
}
function gatherSubMenuChildrenOf(command, editor, commandType, prefix = '') {
  return command.subMenu.map(subCommand => ({
    id: 'commandId' in command ? command.commandId : command.id,
    label: `${prefix}${I18n.text(subCommand.labelKey)}`,
    text: `${prefix}${I18n.text(subCommand.labelKey)}`,
    onClick: () => command.onClick(editor, subCommand, commandType, true)
  }));
}
export function commandToTinymceMenuOption(command, editor, commandType, brandVoiceOptions, flatten = false) {
  const isBrandVoiceCommand = getIsBrandVoiceCommand(command);
  const menuOption = {
    id: 'commandId' in command ? command.commandId : command.id,
    text: isBrandVoiceCommand ? /*#__PURE__*/_jsx(BrandVoiceMenuOptionLabel, {
      command: command,
      brandVoiceOptions: brandVoiceOptions,
      editorSettingsAppUrl: editor.settings.appUrl
    }) : I18n.text(command.labelKey),
    // Note button toolbar requires "text"
    label: isBrandVoiceCommand ? /*#__PURE__*/_jsx(BrandVoiceMenuOptionLabel, {
      command: command,
      brandVoiceOptions: brandVoiceOptions,
      editorSettingsAppUrl: editor.settings.appUrl
    }) : I18n.text(command.labelKey),
    // ... but insert menu requres "label"
    child: undefined,
    onClick: () => command.onClick(editor, command, commandType, true),
    disabled: command.disabled
  };
  if (flatten && 'subMenu' in command && command.subMenu) {
    return gatherSubMenuChildrenOf(command, editor, commandType, `${menuOption.text}: `);
  } else if ('subMenu' in command && command.subMenu) {
    menuOption.onClick = () => {};
    menuOption.child = () => gatherSubMenuChildrenOf(command, editor, commandType);
  }
  return menuOption;
}
function GenerateLabel() {
  return /*#__PURE__*/_jsx(Text, {
    as: "h6",
    paddingBlock: "200",
    paddingInline: "500",
    margin: "0",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "TinymcePlugins.hscopilotcollaboration.toolbar.generateLabel"
    })
  });
}
function AddLabel() {
  return /*#__PURE__*/_jsx(Text, {
    as: "h6",
    paddingBlock: "200",
    paddingInline: "500",
    margin: "0",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "TinymcePlugins.hscopilotcollaboration.toolbar.addLabel"
    })
  });
}
const addDivider = {
  name: 'add',
  disabled: true,
  render: AddLabel,
  label: /*#__PURE__*/_jsx(AddLabel, {}),
  use: 'unstyled',
  id: '__hs-tinymce-ai-assistant-settings'
};
const generateDivider = {
  name: 'generate',
  disabled: true,
  render: GenerateLabel,
  label: /*#__PURE__*/_jsx(GenerateLabel, {}),
  use: 'unstyled',
  id: '__hs-tinymce-ai-assistant-settings'
};
export const addControls = (editor, isDisabled, highlightCommands, slashCommands, brandVoiceOptions, isSuperAdmin) => {
  var _editor$settings$hsPl;
  const flattenedHighlightMenuItems = highlightCommands.flatMap(c => commandToTinymceMenuOption(c, editor, 'highlight', brandVoiceOptions, true));
  const slashMenuItems = slashCommands.flatMap(c => commandToTinymceMenuOption(c, editor, 'slash', brandVoiceOptions));
  if (isUngated(editor, gate('BreezeAI:Rebranding'))) {
    slashMenuItems.unshift(generateDivider);
  }
  const aiDynamicTokensPlugin = (_editor$settings$hsPl = editor.settings.hsPlugins) === null || _editor$settings$hsPl === void 0 ? void 0 : _editor$settings$hsPl[AI_DYNAMIC_TOKEN_SETTINGS];
  const canUseAIDynamicTokens = aiDynamicTokensPlugin === null || aiDynamicTokensPlugin === void 0 ? void 0 : aiDynamicTokensPlugin.useAIDynamicTokens;
  if (canUseAIDynamicTokens) {
    const canInsertDynamicTokens = aiDynamicTokensPlugin === null || aiDynamicTokensPlugin === void 0 ? void 0 : aiDynamicTokensPlugin.canInsertDynamicTokens;
    const dynamicTextMenuItem = {
      id: 'dynamic-text',
      text: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "TinymcePlugins.hsaidynamictokens.button"
        }), /*#__PURE__*/_jsx(Badge, {
          use: "beta",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "TinymcePlugins.hsaidynamictokens.betaBadge"
          })
        })]
      }),
      label: I18n.text('TinymcePlugins.hsaidynamictokens.button'),
      onClick: () => {
        editor.fire(OPEN_AI_DYNAMIC_TOKEN_MODAL_ACTION);
      },
      disabled: !canInsertDynamicTokens,
      tooltip: canInsertDynamicTokens ? undefined : {
        title: I18n.text('TinymcePlugins.hsaidynamictokens.disabledTooltip')
      }
    };
    slashMenuItems.unshift(addDivider, dynamicTextMenuItem);
  }
  const highlightMenuItems = highlightCommands.flatMap(c => commandToTinymceMenuOption(c, editor, 'highlight', brandVoiceOptions));
  if (isSuperAdmin) {
    const divider = {
      render: () => /*#__PURE__*/_jsx(HR, {
        distance: "xs"
      })
    };
    const settingsOption = {
      name: 'settings',
      icon: 'settings',
      text: /*#__PURE__*/_jsx(Link, {
        external: true,
        href: getSettingsLink(editor),
        onClick: () => {
          handleTrackSettingsClick(editor);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "TinymcePlugins.hscontentgeneration.toolbar.settings"
        })
      }),
      label: /*#__PURE__*/_jsx(Link, {
        external: true,
        href: getSettingsLink(editor),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "TinymcePlugins.hscontentgeneration.toolbar.settings"
        })
      }),
      id: '__hs-tinymce-ai-assistant-settings'
    };
    flattenedHighlightMenuItems.push(settingsOption);
    slashMenuItems.push(divider, settingsOption);
    highlightMenuItems.push(divider, settingsOption);
  }
  const buildMenuChildren = () => {
    const isHighlightingText = getIsHighlightingText(editor);
    const result = isHighlightingText ? flattenedHighlightMenuItems : slashMenuItems;
    return result;
  };
  tinymce.canvas.addMenuItem(editor, CANVAS_COPILOT_COLLABORATION, {
    text: I18n.text('TinymcePlugins.hscopilotcollaboration.menuItem'),
    disabled: isDisabled,
    child: isDisabled ? undefined : buildMenuChildren
  });
  tinymce.canvas.addButton(editor, CANVAS_COPILOT_COLLABORATION, {
    renderControl: props => {
      var _editor$settings$gate;
      const rest = _objectWithoutPropertiesLoose(props, _excluded2);
      const isHighlightingText = getIsHighlightingText(editor);
      const menuItems = isHighlightingText ? highlightMenuItems : slashMenuItems;
      const gates = (_editor$settings$gate = editor.settings.gates) !== null && _editor$settings$gate !== void 0 ? _editor$settings$gate : [];
      return /*#__PURE__*/_jsx(NoAuthGatesProvider, {
        gates: gates,
        children: /*#__PURE__*/_jsx(GenAndSelectToolbarButton, Object.assign({}, rest, {
          editor: editor,
          menuItems: menuItems,
          isDisabled: isDisabled
        }))
      });
    }
  });
};